export default function TulsaTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Tulsa Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Tulsa travelers actually move helps us anticipate the passport urgencies that arise here. From energy conference sprints to mission trip group coordination, Northeast Oklahoma travel operates on a different rhythm than most U.S. markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* TUL Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">TUL — Tulsa International Airport</h3>
                <p className="text-gray-400 text-xs">Northeast Tulsa, 7 miles from downtown</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Tulsa International Airport (TUL) serves Northeast Oklahoma with nonstop service to major domestic hubs including Dallas, Denver, Chicago, Atlanta, and Houston — with international connections throughout. Energy executives frequently use TUL for last-minute bookings to Houston and onward to international energy markets in Dubai, London, and Calgary. The airport's compact size means check-in can be quick, but that does not help if your passport is expired.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Many Tulsa energy professionals book international travel through Houston or Dallas hubs. Even if your first leg is domestic, international connections require a valid passport — check yours before booking.
              </p>
            </div>
          </div>

          {/* Energy Industry Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-oil-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Energy Industry Business Travel</h3>
                <p className="text-gray-400 text-xs">Williams, ONEOK, downtown Tulsa energy corridor</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Tulsa's downtown energy corridor is home to some of the largest midstream and pipeline companies in North America. Deal-driven travel to Houston, Calgary, Abu Dhabi, and London happens on short notice — sometimes 72 hours or less. We regularly process urgent passport renewals for energy executives whose passports expired during COVID-era travel lulls. The combination of high travel frequency and compressed booking timelines makes Tulsa energy professionals one of our most time-sensitive client categories.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Energy professionals who travel internationally more than twice a year should consider a second passport to allow continuous travel while one passport is being processed. We handle second passport applications too.
              </p>
            </div>
          </div>

          {/* Church Mission Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-heart-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Church Mission Trips &amp; Group Travel</h3>
                <p className="text-gray-400 text-xs">ORU, Jenks, Bixby &amp; Broken Arrow churches</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Tulsa's thriving faith community generates significant group passport demand, particularly in spring and summer. ORU's global mission programs send students to Africa, Southeast Asia, and Latin America. Local churches in Jenks, Bixby, and Broken Arrow organize group trips to Guatemala, Honduras, and Costa Rica. Group leaders often confirm participant rosters 4–6 weeks before departure, then discover that multiple passports have expired or that children have never had one. We coordinate group processing and child passport requirements to keep entire mission teams on schedule.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Church group leaders: audit every participant's passport as soon as the travel roster is finalized — not the week before departure. Child passports require both parents to appear together or submit a notarized consent form.
              </p>
            </div>
          </div>

          {/* College & Study Abroad */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">ORU &amp; TU Study Abroad Programs</h3>
                <p className="text-gray-400 text-xs">Oral Roberts University &amp; University of Tulsa</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Oral Roberts University and the University of Tulsa together generate significant study-abroad passport demand. ORU's Global Learning program sends students to partner universities and ministry sites worldwide, while TU's petroleum engineering and business programs have strong international placement rates. Study-abroad application deadlines typically fall in October and February, but many students do not think about their passport until after acceptance. We guide ORU and TU students through the acceptance clerk process with a personalized checklist so they do not miss orientation.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (many ORU and TU students) require an in-person acceptance clerk visit. We guide Tulsa students through the exact location, forms, and timeline so they do not miss their program orientation.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">BOK Center &amp; Corporate Event Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Tulsa's BOK Center and Cox Business Center host major industry conferences and corporate events that draw international business travel. Event organizers, keynote speakers, and corporate attendees flying in from abroad — or Tulsa executives traveling to partner events overseas — sometimes discover passport issues during event planning. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}